import { useRef } from 'react';
import { useFileHandlers } from '../../hooks/useFileHandlers.js';
import { useRobotConnection } from '../../hooks/useRobotConnection.js';
import { useVisualizer } from '../../context/AppContext.jsx';

export default function FileUploadSection({ onFileLoaded, onInstallationLoaded, onWaypointsLoaded }) {
  const fileInputRef = useRef(null);
  const installationInputRef = useRef(null);
  const { handleFile, handleInstallationFile, loadTestData, loadTestData2 } = useFileHandlers();
  const { connectToRobotFTP } = useRobotConnection();
  const { isServerAvailable } = useVisualizer();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await handleFile(file);
      onFileLoaded?.();
      onWaypointsLoaded?.();
    }
  };

  const handleInstallationChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await handleInstallationFile(file);
      onInstallationLoaded?.();
    }
  };

  const handleDrop = async (e, type) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      if (type === 'urp' && file.name.endsWith('.urp')) {
        await handleFile(file);
        onFileLoaded?.();
        onWaypointsLoaded?.();
      } else if (type === 'installation' && file.name.endsWith('.installation')) {
        await handleInstallationFile(file);
        onInstallationLoaded?.();
      }
    }
  };

  return (
    <section className="mb-6">
      <h2 className="text-lg font-semibold mb-4">Load Files</h2>
      
      {/* URP File Upload */}
      <div
        className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
        onDragOver={(e) => { e.preventDefault(); }}
        onDragLeave={(e) => { e.preventDefault(); }}
        onDrop={(e) => handleDrop(e, 'urp')}
        onClick={() => fileInputRef.current?.click()}
      >
        <svg className="w-16 h-16 mx-auto mb-4 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <p className="text-text-primary mb-2">Drop .urp file here</p>
        <p className="text-text-secondary text-sm mb-2">or</p>
        <button className="bg-primary text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors">
          Browse Program
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".urp"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* Installation File Upload */}
      <div
        className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors mt-4"
        onDragOver={(e) => { e.preventDefault(); }}
        onDragLeave={(e) => { e.preventDefault(); }}
        onDrop={(e) => handleDrop(e, 'installation')}
        onClick={() => installationInputRef.current?.click()}
      >
        <svg className="w-12 h-12 mx-auto mb-2 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <p className="text-text-secondary text-sm mb-2">Drop .installation file</p>
        <p className="text-text-secondary text-xs mb-2">or</p>
        <button className="bg-primary text-white px-3 py-1.5 rounded text-sm hover:bg-blue-600 transition-colors">
          Browse Installation
        </button>
        <input
          ref={installationInputRef}
          type="file"
          accept=".installation"
          className="hidden"
          onChange={handleInstallationChange}
        />
      </div>

      {/* Action Buttons */}
      <div className="mt-4 flex flex-col gap-2">
        {isServerAvailable && (
          <button
            onClick={async () => {
              await connectToRobotFTP();
              onFileLoaded?.();
              onInstallationLoaded?.();
              onWaypointsLoaded?.();
            }}
            className="w-full bg-primary text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" viewBox="0 0 17.647 17.647" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <g>
                <g>
                  <circle cx="2.648" cy="7.546" r="1.11"></circle>
                  <path d="M5.296,7.546c0-1.46-1.188-2.648-2.648-2.648C1.189,4.898,0,6.086,0,7.546s1.188,2.648,2.648,2.648 C4.108,10.194,5.296,9.006,5.296,7.546z M2.648,9.567c-1.114,0-2.021-0.906-2.021-2.021s0.907-2.021,2.021-2.021 c1.115,0,2.021,0.906,2.021,2.021S3.762,9.567,2.648,9.567z"></path>
                  <path d="M5.655,9.221C5.527,9.576,5.324,9.91,5.041,10.194c-0.306,0.305-0.668,0.516-1.052,0.641 l2.896,2.898h-0.56v2.035h5.435v-2.035h-1.596L5.655,9.221z"></path>
                  <path d="M17.572,4.686c-0.104-0.104-0.271-0.107-0.376-0.004l-2.418,2.364l-1.78-0.934l-0.467-2.034 l1.192-1.618l3.206,1.073c0.049,0.016,0.099,0.018,0.146,0.007c0.087-0.02,0.162-0.083,0.191-0.175 c0.049-0.139-0.027-0.291-0.166-0.337l-3.391-1.135c-0.11-0.038-0.23,0.001-0.298,0.094l-1.085,1.47l-0.895,0.205l0.126,0.548 L5.294,5.648c0.232,0.297,0.407,0.646,0.497,1.038c0.096,0.42,0.085,0.839-0.014,1.232l6.298-1.447l0.126,0.548L13.154,6.8 l-0.002-0.004L14.7,7.609c0.059,0.03,0.124,0.038,0.184,0.023c0.047-0.011,0.092-0.034,0.126-0.069l2.556-2.501 C17.672,4.96,17.674,4.791,17.572,4.686z"></path>
                </g>
              </g>
            </svg>
            Load from Robot
          </button>
        )}
        <button 
          onClick={async () => {
            await loadTestData();
            onFileLoaded?.();
            onInstallationLoaded?.();
            onWaypointsLoaded?.();
          }}
          className="w-full bg-bg-tertiary text-text-primary px-4 py-2 rounded hover:bg-bg-hover transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
          Load Sample Files (Small)
        </button>
        <button 
          onClick={async () => {
            await loadTestData2();
            onFileLoaded?.();
            onInstallationLoaded?.();
            onWaypointsLoaded?.();
          }}
          className="w-full bg-bg-tertiary text-text-primary px-4 py-2 rounded hover:bg-bg-hover transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
          Load Sample Files (Large)
        </button>
      </div>
    </section>
  );
}

